/*
 * Copyright (c) 2024 Paul Ostrowski. All rights reserved.
 *
 * This software is the proprietary information of Paul Ostrowski.
 * Unauthorized copying, modification, or distribution of this code, via any medium, is strictly prohibited.
 * This code is proprietary and confidential.
 *
 * For licensing inquiries, contact Paul Ostrowski at postrowski@gmail.com.
*/

import React, { useState, useRef, useEffect, ChangeEvent, KeyboardEvent } from 'react';
import ReactMarkdown from 'react-markdown';
//import remarkGfm from 'remark-gfm';

interface EditableTextProps {initialValue: string | number;
                             min?: number;
                             max?: number;
                             width?: string;
                             type?: 'text' | 'number' | 'textarea';
                             displayProcessor?: (value: any) => any;
                             alwaysShow?: boolean;
                             onSave: (newValue: string | number) => void;
                             useMarkdown?: boolean;
}

const EditableText: React.FC<EditableTextProps> = ({initialValue,
                                                    min,
                                                    max,
                                                    width = '100%',
                                                    type = 'text',
                                                    displayProcessor,
                                                    alwaysShow = false,
                                                    onSave,
                                                    useMarkdown = false,
                                                  }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [value, setValue] = useState<string | number>(initialValue);
  const [inputStyle, setInputStyle] = useState<React.CSSProperties>({});
  const textRef = useRef<HTMLSpanElement>(null); // Ref for the text element
  const inputRef = useRef<HTMLInputElement>(null); // the <input> while editing (for the wheel handler)
  // Keep the latest onSave without re-attaching the native wheel listener every render.
  const onSaveRef = useRef(onSave);
  useEffect(() => { onSaveRef.current = onSave; }, [onSave]);

  // Sync internal state with external initialValue changes — but NOT while the user is actively editing.
  // onSave fires on every keystroke, so the parent re-renders with a new initialValue mid-edit; syncing
  // that back would reassign the input's value and bounce the caret to the end between keystrokes. While
  // editing, local `value` is the source of truth; we re-sync once editing ends (blur).
  useEffect(() => {
    if (!isEditing) {
      setValue(initialValue);
    }
  }, [initialValue, isEditing]);

  useEffect(() => {
    const handleKeyDown = (event: globalThis.KeyboardEvent): void => {
      if (event.key === "Escape") {
        setIsEditing(false);
      }
    };
  
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  // For a number field, the mouse wheel nudges the value by 1 (up = increase) instead of scrolling the
  // page out from under the cursor. A native, NON-passive listener is required so preventDefault() can
  // actually cancel the page scroll — React's onWheel is passive and can't. preventDefault also cancels
  // the browser's own number-input wheel-increment, so we apply exactly one step ourselves.
  useEffect(() => {
    if (!isEditing || type !== 'number') {
      return;
    }
    const el = inputRef.current;
    if (!el) {
      return;
    }
    const handleWheel = (e: WheelEvent) => {
      e.preventDefault();
      const cur = parseInt(el.value, 10);
      let next = (Number.isNaN(cur) ? 0 : cur) + (e.deltaY < 0 ? 1 : -1);
      if (min != null) next = Math.max(min, next);
      if (max != null) next = Math.min(max, next);
      setValue(next);
      onSaveRef.current(next);
    };
    el.addEventListener('wheel', handleWheel, { passive: false });
    return () => el.removeEventListener('wheel', handleWheel);
  }, [isEditing, type, min, max]);

  const handleInputChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    let val: string | number = event.target.value;
    if (type === 'number') {
      val = parseInt(val);
      if (min != null) {
        val = Math.max(min, val);
      }
      if (max != null) {
        val = Math.min(max, val);
      }
    }
    setValue(val);
    onSave(val);
  };

  const handleInputBlur = () => {
    setIsEditing(false);
    onSave(value);
  };

  const handleKeyPress = (event: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && type !== 'textarea') {
      setIsEditing(false);
      onSave(value);
    }
  };

  const handleClickSpan = () => {
    if (textRef.current) {
      const computedStyle = window.getComputedStyle(textRef.current);
      setInputStyle({
        fontFamily: computedStyle.fontFamily,
        fontSize: computedStyle.fontSize,
        fontStyle: computedStyle.fontStyle,
        fontWeight: computedStyle.fontWeight as React.CSSProperties['fontWeight'], // Ensure correct type
        textAlign: computedStyle.textAlign as React.CSSProperties['textAlign'],   // Ensure correct type
        });
    }
    setIsEditing(true);
  };

  let content: JSX.Element | string | number = value;
  if (typeof content === 'string') {
    content = content.trim();
  }
  if (content && displayProcessor) {
    content = displayProcessor(content);
  }
  if (alwaysShow && !content && content !== 0) {
    content = '\u00A0 -- \u00A0';
  }

  if (useMarkdown === true) {
    content = <ReactMarkdown 
      //remarkPlugins={[remarkGfm]}
      // Disable HTML to prevent raw HTML injection
      disallowedElements={['script', 'iframe', 'img']} 
      unwrapDisallowed={true}
    >{String(content)}</ReactMarkdown>;
  }

  return (
    <span style={{ position: 'relative' }} className='edit-field'>
      {isEditing ? (
        type === 'textarea' ? (
          <textarea
            className="editableTextInput"
            value={String(value)}
            onChange={handleInputChange}
            onBlur={handleInputBlur}
            onKeyDown={handleKeyPress}
            autoFocus
            style={{ ...inputStyle, width, height: '75px' }}
          />
        ) : (
          <input
            ref={inputRef}
            type={type}
            className="editableTextInput"
            value={String(value)}
            onChange={handleInputChange}
            onBlur={handleInputBlur}
            onKeyDown={handleKeyPress}
            autoFocus
            style={{ ...inputStyle, width }}
          />
        )
      ) : (
        <span onClick={handleClickSpan} ref={textRef}>
          {content}
        </span>
      )}
    </span>
  );
};

export default EditableText;